<template>
  <div
    class="voucher"
    :class="active ? 'voucher__active' : 'voucher__inactive'"
  >
    <div class="voucher__body">
      <div class="voucher__body-data">
        <div class="voucher__body-data__id">
          VOUCHER: {{ shortenUUIDs(voucher.id) }}
        </div>
        <div class="voucher__body-data__price">
          Expires: {{ formatDate(voucher.expiration_date) }}
        </div>
        <div class="voucher__body-data__price">
          Purchased: {{ formatDate(voucher.created) }}
        </div>
      </div>
      <div class="voucher__body__attempts">
        <div
          class="voucher__body__attempts-table"
          v-if="
            (voucher.active_attempts && voucher.active_attempts.length) ||
              (voucher.inactive_attempts && voucher.inactive_attempts.length)
          "
        >
          <div
            class="voucher__body__attempts-table-row voucher__body__attempts-table-header"
          >
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__date-started"
            >
              Exam started
            </div>
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__date-submitted"
            >
              Submitted
            </div>
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__status"
            >
              Status
            </div>
          </div>
          <div
            class="voucher__body__attempts-table-row"
            v-for="attempt in voucher.active_attempts"
            :key="attempt.id"
          >
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__date-started"
            >
              <span>{{ formatDateWithHour(attempt.created) }}</span>
            </div>
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__date-submitted"
            >
              <span>{{
                attempt.finished_at
                  ? formatDateWithHour(attempt.finished_at)
                  : '-'
              }}</span>
            </div>
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__status"
            >
              <div class="voucher__body__attempts-status">
                <span
                  v-if="friendlyStatusClasses(attempt) !== 'started'"
                  :class="
                    `voucher__body__attempts-status--${friendlyStatusClasses(
                      attempt
                    )}`
                  "
                ></span>
                {{ friendlyStatus(attempt) }}
              </div>
            </div>
            <button
              v-if="isModalViewable(attempt)"
              @click="showAttemptScore(attempt)"
              class="voucher__body__dots-wrapper"
            >
              <DotIcon class="voucher__body__dot" />
              <DotIcon class="voucher__body__dot" />
              <DotIcon class="voucher__body__dot" />
            </button>
          </div>
          <div
            class="voucher__body__attempts-table-row voucher__body__attempts-table-row--inactive"
            v-for="attempt in voucher.inactive_attempts"
            :key="attempt.id"
          >
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__date-started"
            >
              <span>{{ formatDateWithHour(attempt.created) }}</span>
            </div>
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__date-submitted"
            >
              <span>{{
                attempt.finished_at
                  ? formatDateWithHour(attempt.finished_at)
                  : '-'
              }}</span>
            </div>
            <div
              class="voucher__body__attempts-table-col voucher__body__attempts-table-col__status"
            >
              <div class="voucher__body__attempts-status">
                <span
                  :class="
                    `voucher__body__attempts-status--${friendlyStatusClasses(
                      attempt
                    )}`
                  "
                ></span>
                {{ friendlyStatus(attempt) }}
                <button
                  v-if="isModalViewable(attempt)"
                  @click="showAttemptScore(attempt)"
                  class="voucher__body__dots-wrapper"
                >
                  <DotIcon class="voucher__body__dot" />
                  <DotIcon class="voucher__body__dot" />
                  <DotIcon class="voucher__body__dot" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="voucher__body__attempts--empty">
          You haven't attempted the certification exam yet.
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { format } from 'date-fns-tz'
import CONSTANTS from '@/constants'
import DotIcon from '@/assets/icons/certifications/dot.svg'

export default {
  name: 'CertificateVoucher',
  components: {
    DotIcon: { ...DotIcon, name: 'DotIcon' },
  },
  props: {
    voucher: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      required: true,
    },
  },
  data: () => {
    return {
      possibleStatus: {
        [CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS]: 'Started',
        [CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.REVIEW]: 'Pending Review',
        [CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.TIMEDOUT]:
          'Failed (Time expired)',
      },
      possibleStatusClasses: {
        [CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS]: 'started',
        [CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.REVIEW]: 'review',
        [CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.TIMEDOUT]: 'failed',
      },
    }
  },
  methods: {
    formatDate(date) {
      return format(new Date(date), 'LLL d, yyyy')
    },
    formatDateWithHour(date) {
      return format(new Date(date), 'LLL d, yyyy  hh:mmaaa')
    },
    friendlyStatus(attempt) {
      const { status } = attempt
      if (status === CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS) {
        return 'In progress'
      }
      if (status !== CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.FINISHED) {
        return this.possibleStatus[status] || ''
      }
      if (this.hasPassed(attempt)) {
        return 'Passed'
      }
      return 'Failed'
    },
    friendlyStatusClasses(attempt) {
      const { status } = attempt
      if (status !== CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.FINISHED) {
        return this.possibleStatusClasses[status] || ''
      }
      if (this.hasPassed(attempt)) {
        return 'passed'
      }
      return 'failed'
    },
    shortenUUIDs(id) {
      return id.split('-')[0]
    },
    isModalViewable(attempt) {
      const { status } = attempt
      const unavailableStatus = [
        CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS,
        CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.REVIEW,
        CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.TIMEDOUT,
      ]
      return !unavailableStatus.includes(status)
    },
    showAttemptScore(attempt) {
      this.$store.dispatch('certifications/setCurrentAttemptScored', attempt)
    },
    hasPassed(attempt) {
      let practice = true
      if (!attempt) return false
      if (attempt.practice_lab) {
        if (attempt.user_lab_attempt) {
          practice = attempt.user_lab_attempt.has_passed
        } else {
          practice = false
        }
      }
      return attempt.has_passed && practice
    },
  },
}
</script>
<style lang="scss" scoped>
.voucher {
  $this: &;
  position: relative;
  background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    )
    var(--dark-brighter);
  &:last-child {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
  &__active-icon {
    background: #48c700;
    width: 18px;
    height: 18px;
    margin: 0 10px 0 0;
    display: inline-block;
    border-radius: 50%;
  }
  &__inactive-icon {
    stroke: var(--white);
    stroke-width: 2px;
    width: 20px;
    height: 30px;
    padding: 4px 0px 0px 1px;
    margin-right: 15px;
    overflow: visible;
  }
  &__inactive {
    #{$this}__header-expiration {
      color: var(--light-blue-gray);
    }
  }
  &__body {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 15px 10px;

    @include tablet {
      flex-direction: row;
      padding: 20px 30px;
    }

    &__dots-wrapper {
      position: absolute;
      right: 0;
      top: 0;
      background: none;
      border: none;
      cursor: pointer;
    }
    &__dot {
      margin: 0 2px 0 0;
    }
    &-data {
      font-size: 16px;
      font-family: var(--font-medium);
      width: 100%;
      z-index: 1;
      padding: 0 5px;

      @include tablet {
        width: 40%;
      }

      @include desktop {
        width: 30%;
      }

      &__id {
        color: var(--white);
        font-size: rem(18px);
        width: 100%;
        margin-bottom: 15px;
        line-height: 24px;
      }
      &__price {
        padding: 0;
        width: 100%;
        color: var(--light-blue-gray);
        font-size: 12px;
        line-height: 14px;

        @include tablet {
          font-size: 16px;
          line-height: 24px;
        }
      }
      &__status {
        padding: 5px 0;
        width: 100%;
        display: flex;
        align-items: center;
      }
    }
    &__attempts {
      font-size: 12px;
      line-height: 14px;
      font-family: var(--font-medium);
      flex: 1;

      @include tablet {
        font-size: 16px;
        line-height: 18px;
      }

      &-title {
        text-transform: uppercase;
        color: var(--light-blue-gray);
      }
      &-table {
        display: table;
        width: 100%;
        border-spacing: 5px;
        border-collapse: collapse;
      }
      &-table-header {
        color: var(--white);
        text-transform: uppercase;
        font-size: rem(12px);
        line-height: 14px;
      }
      &-table-row {
        display: table-row;
        width: auto;
        clear: both;
        padding: 0 5px;

        &:hover .voucher__body__attempts-table-col {
          background: #323942;

          &:last-child {
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
          }

          &:first-child {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
          }
        }

        &--inactive {
          color: var(--light-blue-gray);
        }
      }
      &-table-col {
        float: left;
        display: table-cell;
        padding: 10px 0;
        height: 48px;

        @include desktop {
          width: 300px;
        }
        &__date-started {
          width: 30%;
          padding-left: 5px;
        }

        &__date-submitted {
          width: 30%;
        }

        &__status {
          width: 40%;
        }
      }
      &-status {
        display: flex;
        align-items: center;
        position: relative;
        span {
          width: 5px;
          min-width: 5px;
          height: 16px;
          margin-right: 8px;
          border-radius: 10px;
        }
        &--passed {
          background: linear-gradient(
            145.56deg,
            #00da99 20.53%,
            #70ffd4 79.76%
          );
        }
        &--failed {
          background: linear-gradient(180deg, #e52a2a 0%, #ff5e5e 100%);
        }
        &--review {
          background: #eeeeee;
        }
        &--started {
          background: var(--light-blue-gray);
        }
        &--timeout {
          background: linear-gradient(180deg, #e52a2a 0%, #ff5e5e 100%);
        }
      }
      &--empty {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 3px 10px;
        width: 100%;
        min-height: 197px;
        background: linear-gradient(
          111.02deg,
          rgba(18, 18, 18, 0.7) 3.86%,
          rgba(18, 18, 18, 0) 107.96%
        );
        border-radius: 10px;
        color: #888888;
        font-size: rem(12px);
      }
    }
  }
}
</style>
